// Renders the clip to a PNG the way heatmap.py lays out its image: tape on
// top, one pixel row per time row, footer text in the bottom-left corner, and
// ytick labels down the left edge when set. Pan and zoom are ignored; the
// width is the caller's choice and columns are max-reduced to fit.

import {
  checkChartHeight,
  drawCheckChart,
  drawCheckColumn,
  drawEdges,
  drawFilterEdges,
  drawStrips,
  layoutStrips,
  stripsHeight,
  type CheckFocus,
  type CheckOverlay,
  type VisibleScheme,
} from '../overlays/draw.ts'
import { buildHeatMask, type ResolvedFilter } from '../overlays/filter.ts'
import { ColorTable, paintView } from './heat.ts'
import { drawFooter, drawShadowText, footerLines, layoutTimeTicks } from './gutter.ts'
import { fullView, type Clip } from './layout.ts'
import type { PaletteName } from './palette.ts'
import { drawTape, layoutTape, TAPE_HEIGHT } from './tape.ts'
import { Pyramid } from './tiles.ts'

/** Past this many rows the canvas is at risk in every browser; the UI says so first. */
export const MAX_EXPORT_ROWS = 16384
export const DEFAULT_EXPORT_WIDTH = 1024

export interface ExportOptions {
  width: number
  palette: PaletteName
  displayZone: string
  ytickSec: number | null
  /** Already resolved against the run; see `clampDbRange`. */
  dbMin: number
  dbMax: number
  /** Scheme strips to draw under the tape, as on screen; their edges continue down the heat. */
  schemes?: readonly VisibleScheme[]
  /** The signal check's chart, as on screen. */
  check?: CheckOverlay | null
  /** The pinned centers, as on screen; drawn only with a chart to sit on. */
  pins?: readonly CheckFocus[]
  /** A receive filter applied to the readings before they are colored, as on screen. */
  filter?: ResolvedFilter | null
}

export interface ExportPlan {
  width: number
  height: number
  rows: number
  /** Height of the scheme strips, 0 when none are visible. */
  strips: number
}

export function planExport(clip: Clip, width: number, schemeCount = 0): ExportPlan {
  const strips = stripsHeight(schemeCount)
  return { width: Math.max(16, Math.round(width)), height: TAPE_HEIGHT + strips + clip.rows + 1, rows: clip.rows, strips }
}

export function renderExport(clip: Clip, pyramid: Pyramid, options: ExportOptions): HTMLCanvasElement {
  const schemes = options.schemes ?? []
  const plan = planExport(clip, options.width, schemes.length)
  const canvas = document.createElement('canvas')
  canvas.width = plan.width
  canvas.height = plan.height
  const ctx = canvas.getContext('2d')!
  const view = fullView(clip)

  // Tape.
  drawTape(ctx, layoutTape(clip, view, plan.width), plan.width, 1)

  // Scheme strips, directly under the tape.
  const measure = (text: string) => ctx.measureText(text).width
  const layouts = schemes.length > 0 ? layoutStrips(schemes, clip, view, plan.width, measure) : []
  if (layouts.length > 0) {
    ctx.save()
    ctx.translate(0, TAPE_HEIGHT)
    drawStrips(ctx, layouts, plan.width, 1)
    ctx.restore()
  }
  const heatTop = TAPE_HEIGHT + plan.strips + 1

  // Heat, one pixel per row, columns max-reduced.
  const image = ctx.createImageData(plan.width, plan.rows)
  const lut = new ColorTable().update(options.palette, options.dbMin, options.dbMax)
  const mask = options.filter ? buildHeatMask(clip, view, plan.width, options.filter) : null
  paintView(
    pyramid,
    view,
    lut,
    { data: new Uint32Array(image.data.buffer), width: plan.width, height: plan.rows },
    mask,
  )
  ctx.putImageData(image, 0, heatTop)

  // Overlays on the heat, exactly as the screen draws them.
  ctx.save()
  ctx.translate(0, heatTop)
  const check = options.check ?? null
  const pins = check ? options.pins ?? [] : []
  pins.forEach((pin, i) => {
    if (check) drawCheckColumn(ctx, clip, view, pin, check, plan.width, plan.rows, 1, true, String(i + 1))
  })
  if (options.filter) drawFilterEdges(ctx, clip, view, options.filter, plan.width, plan.rows, 1)
  if (layouts.length > 0) drawEdges(ctx, layouts, plan.rows, 1)
  if (check) drawCheckChart(ctx, clip, view, check, null, pins, plan.width, plan.rows, 1)
  ctx.restore()

  // Time labels over the image at the left edge, as heatmap.py draws them.
  let margin = 2
  if (options.ytickSec !== null) {
    margin = 60
    for (const tick of layoutTimeTicks(clip, view, plan.rows, options.displayZone, options.ytickSec)) {
      drawShadowText(ctx, 2, heatTop + Math.round(tick.y), tick.text)
    }
  }

  // The profile chart owns the foot of the plot, so the caption sits above it
  // rather than across it.
  const chart = options.check ? checkChartHeight(plan.rows) : 0
  drawFooter(ctx, footerLines(clip, options.displayZone), margin, plan.height - chart)
  return canvas
}

export function exportFileName(clip: Clip, displayZone: string): string {
  const started = footerLines(clip, displayZone).started.replace('Started: ', '').replace(/[: ]/g, '-').replace(/Z$/, '')
  return `spectrum-mapper-${started}.png`
}
